import { useEffect, useRef } from "react";
import classnames from "classnames";
import { Link } from "react-router-dom";
import { ReactComponent as UGGLogo } from "@ugg/shared/assets/svg/logos/ugg-logo-blue.svg";
import { SvgGameVal } from "@ugitgud/legos/ui/icon/SVG/GameVal";
import { SvgGameLol } from "@ugitgud/legos/ui/icon/SVG/GameLol";
import { SvgGameDeadlock } from "@ugitgud/legos/ui/icon/SVG/GameDeadlock";
import { ReactComponent as RightArrowHead } from "svg/right-arrow-head.svg";
import { NavTag } from "@ugitgud/legos/ui/form/tag/NavTag";

function useVideoLoop(videoRef: HTMLVideoElement | null, loops: number) {
  const currLoops = useRef(1);

  useEffect(() => {
    function endedHandler() {
      if (videoRef && currLoops.current < loops) {
        videoRef.play();
        currLoops.current++;
      }
    }
    videoRef?.addEventListener("ended", endedHandler);

    return () => {
      videoRef?.removeEventListener("ended", endedHandler);
    };
  }, [videoRef, loops]);
}

export function WowPromo(props: { className?: string }) {
  return (
    <div className={classnames("relative flex items-center font-['Inter'] overflow-hidden", props.className)}>
      <div className="absolute flex flex-col left-0 top-0 bottom-0 ml-[18px] z-[2] pt-[24px] pb-[18px]">
        <div className="flex items-center text-[19px] font-semibold leading-[20px]">
          <img src="/images/ugg/games/WoW-detailed-icon.png" width={20} />
          <div className="flex item-center ml-[10px] h-[20px]">
            <UGGLogo className="w-[50px]" />
          </div>
          <span className="relative ml-[7px] top-[1px]">has WoW</span>
        </div>
        <div className="flex flex-col mt-[18px] mb-[20px] font-bold text-[24px] leading-[32px] text-white">
          Data Driven.
          <br />
          Always Best in Slot.
        </div>
        <a
          className={`
            flex items-center justify-center w-max mt-auto py-[10px] pl-[12px] pr-[8px] 
            rounded-[3px] font-bold text-[13px] leading-[16px] text-white 
            bg-accent-blue-400 hover:bg-accent-blue-600 cursor-pointer
          `}
          href="https://u.gg/wow/tier-list/dps-rankings/raid?utm_source=UGG&utm_campaign=BuildPage"
        >
          Check it Out
          <RightArrowHead className="ml-[6px] w-[16px] h-[16px]" />
        </a>
      </div>
      <video playsInline autoPlay loop muted className="h-full object-cover">
        <source type="video/webm" src="https://static.bigbrain.gg/assets/ugg/pages/champion/wowbgloop.webm" />
      </video>
      <div className="absolute top-0 w-full h-full z-[1] bg-[linear-gradient(270deg,rgba(25,25,55,0)_0%,#191937_100%)]" />
    </div>
  );
}

export function ValorantAppPromo(props: { className?: string }) {
  return (
    <div className={classnames("relative flex items-center font-['Inter'] overflow-hidden bg-purple-400", props.className)}>
      <div className="absolute flex flex-col left-0 top-0 bottom-0 ml-[18px] z-[2] pt-[24px] pb-[18px]">
        <div className="flex items-center text-[19px] font-semibold leading-[20px]">
          <SvgGameVal className="w-[20px]" />
          <div className="flex item-center ml-[10px] h-[20px]">
            <UGGLogo className="w-[50px]" />
          </div>
          <span className="relative ml-[7px] top-[1px]">Valorant App</span>
          <div className="relative top-[1px] flex items-center ml-[5px] px-[4px] h-[14px] rounded-[1px] bg-accent-forest-green-500 font-bold text-[10px]">
            BETA
          </div>
        </div>
        <div className="flex flex-col mt-[18px] mb-[20px] font-bold text-[24px] leading-[32px] text-white">
          Peek Maps,
          <br />
          Scouting & More
        </div>
        <a
          className={`
            flex items-center justify-center w-max mt-auto py-[10px] pl-[12px] pr-[8px] 
            rounded-[3px] font-bold text-[13px] leading-[16px] text-white 
            bg-accent-blue-400 hover:bg-accent-blue-600 cursor-pointer
          `}
          href="https://u.gg/val/app?utm_source=UGG&utm_campaign=BuildPage"
        >
          Download Now
          <RightArrowHead className="ml-[6px] w-[16px] h-[16px]" />
        </a>
      </div>
      <video playsInline autoPlay loop muted className="h-full object-cover ml-[73px]">
        <source type="video/webm" src="https://static.bigbrain.gg/assets/ugg/pages/champion/valorant-app-promo.webm" />
      </video>
      <div className="absolute top-0  left-[73px] w-full h-full z-[1] bg-[linear-gradient(270deg,rgba(25,25,55,0)_0%,#191937_100%)]" />
    </div>
  );
}

export function LoLAppPromo(props: { className?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  useVideoLoop(videoRef.current, 3);

  return (
    <div
      className={classnames(
        "relative flex items-center font-['Inter'] overflow-hidden bg-purple-400 min-h-[230px] max-h-[265px]",
        props.className,
      )}
    >
      <div className="absolute flex flex-col left-0 top-0 bottom-0 ml-[18px] z-[2] pt-[24px] pb-[18px]">
        <div className="flex items-center text-[19px] font-semibold leading-[20px]">
          <SvgGameLol className="w-[20px]" />
          <div className="flex item-center ml-[10px] h-[20px]">
            <UGGLogo className="w-[50px]" />
          </div>
          <span className="relative ml-[7px] top-[1px]">Desktop App</span>
        </div>
        <div className="flex flex-col mt-[18px] mb-[20px] font-bold text-[24px] leading-[32px] text-white">
          Never set runes again.
          <br />
          Auto-import runes,
          <br />
          items and skill order.
        </div>
        <Link
          className={`
            flex items-center justify-center w-max mt-auto py-[10px] pl-[12px] pr-[8px] 
            rounded-[3px] font-bold text-[13px] leading-[16px] text-white 
            bg-accent-red-500 hover:bg-accent-red-700 cursor-pointer
          `}
          to="/app?utm_source=UGG&utm_campaign=BuildPage"
        >
          Download App
          <RightArrowHead className="ml-[6px] w-[16px] h-[16px]" />
        </Link>
      </div>
      <video ref={videoRef} playsInline autoPlay muted className="h-full object-cover ml-[73px]">
        <source type="video/webm" src="https://static.bigbrain.gg/assets/ugg/pages/desktop-app/ugg-standalone-app.webm" />
      </video>
      <div className="absolute top-0  left-[73px] w-full h-full z-[1] bg-[linear-gradient(270deg,rgba(25,25,55,0)_0%,#191937_100%)]" />
    </div>
  );
}

export function DeadlockPromo(props: { className?: string }) {
  return (
    <div
      className={classnames(
        "relative flex items-center overflow-hidden bg-purple-400 min-h-[230px] rounded-br-[3px]",
        props.className,
      )}
    >
      <div className="absolute flex flex-col left-0 top-0 bottom-0 ml-[18px] z-[2] pt-[24px] pb-[18px]">
        <div className="flex items-center text-[19px] font-semibold leading-[20px]">
          <SvgGameDeadlock className="w-[20px]" />
          <div className="flex item-center ml-[10px] h-[20px]">
            <UGGLogo className="w-[50px]" />
          </div>
          <span className="relative ml-[7px] top-[1px]">Deadlock App</span>
          <NavTag shrink={false} size="sm" colorScheme="primary" className="ml-1.5 py-0.5 px-1 bg-accent-blue-400 rounded-sm">
            NEW
          </NavTag>
        </div>
        <div className="flex flex-col mt-[18px] mb-[20px] font-bold text-[24px] leading-[32px] text-white">
          Player Scouting,
          <br />
          In-game overlays
          <br />& more.
        </div>
        <Link
          className={`
            flex items-center justify-center w-max mt-auto py-[10px] pl-[12px] pr-[8px] 
            rounded-[3px] font-bold text-[13px] leading-[16px] text-white 
            bg-accent-red-500 hover:bg-accent-red-700 cursor-pointer
          `}
          to="/app?game=deadlock&utm_source=UGG&utm_campaign=BuildPage"
        >
          Download App
          <RightArrowHead className="ml-[6px] w-[16px] h-[16px]" />
        </Link>
      </div>
      <img className="h-full object-cover" src="https://static.bigbrain.gg/assets/ugg/pages/champion/deadlock-promo.webp" />
    </div>
  );
}
